/**
 * Reusable Number Input Component
 * Prevents alphabet input and handles integer/decimal validation
 */

import React, { forwardRef, useState } from 'react';
import Input, { InputProps } from './Input';

export interface NumberInputProps extends Omit<InputProps, 'type' | 'onChange' | 'value'> {
  value?: number | null;
  onChange?: (value: number | null) => void;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      allowDecimals = false,
      allowNegative = false,
      min,
      max,
      step = allowDecimals ? 0.01 : 1,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState<string>(
      value !== null && value !== undefined ? String(value) : ''
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      if (allowDecimals) {
        // Allow decimals: remove all non-numeric characters except decimal point
        // Also allow minus sign at the start if negatives are allowed
        let cleanedValue = inputValue;
        
        if (allowNegative) {
          // Allow minus sign only at the start
          cleanedValue = cleanedValue.replace(/[^0-9.-]/g, '');
          // Ensure only one minus at the start
          if (cleanedValue.startsWith('-')) {
            cleanedValue = '-' + cleanedValue.substring(1).replace(/-/g, '');
          } else {
            cleanedValue = cleanedValue.replace(/-/g, '');
          }
        } else {
          // Remove all non-numeric characters except decimal point
          cleanedValue = cleanedValue.replace(/[^0-9.]/g, '');
        }
        
        // Ensure only one decimal point
        const parts = cleanedValue.split('.');
        if (parts.length > 2) {
          cleanedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        setDisplayValue(cleanedValue);
        const numValue = cleanedValue === '' || cleanedValue === '-' ? null : parseFloat(cleanedValue);
        onChange?.(numValue !== null && !isNaN(numValue) ? numValue : null);
      } else {
        // Integer only: remove all non-numeric characters
        // Also allow minus sign at the start if negatives are allowed
        let cleanedValue = inputValue;
        
        if (allowNegative) {
          // Allow minus sign only at the start
          cleanedValue = cleanedValue.replace(/[^0-9-]/g, '');
          // Ensure only one minus at the start
          if (cleanedValue.startsWith('-')) {
            cleanedValue = '-' + cleanedValue.substring(1).replace(/-/g, '');
          } else {
            cleanedValue = cleanedValue.replace(/-/g, '');
          }
        } else {
          // Remove all non-numeric characters
          cleanedValue = cleanedValue.replace(/[^0-9]/g, '');
        }
        
        setDisplayValue(cleanedValue);
        const numValue = cleanedValue === '' || cleanedValue === '-' ? null : parseInt(cleanedValue, 10);
        onChange?.(numValue !== null && !isNaN(numValue) ? numValue : null);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Only block 'e', 'E', '+' keys (scientific notation) - everything else is handled by onChange filtering
      if (e.key === 'e' || e.key === 'E' || e.key === '+') {
        e.preventDefault();
        return;
      }
      
      // Call parent's onKeyDown if provided
      onKeyDown?.(e);
    };

    // Sync with external value changes
    React.useEffect(() => {
      if (value !== null && value !== undefined) {
        setDisplayValue(String(value));
      } else {
        setDisplayValue('');
      }
    }, [value]);

    return (
      <Input
        ref={ref}
        type="text"
        inputMode={allowDecimals ? 'decimal' : 'numeric'}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        step={step}
        min={min}
        max={max}
        {...props}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;

